// Ctrl+V でクリップボードに画像があれば配置、テキストが URL なら
// （選択中の付箋・テキスト図形があれば本文をリンク化、無ければ）OGP リンクカード化、
// text/html にハイパーリンクがあれば [表示文字](URL) 形式へ変換したテキスト図形として配置、
// それ以外の空でないテキストならテキスト図形として配置する。
// 配置位置は最後に追跡したマウス位置（lastPointerStagePosition）。

import { useEffect } from "react";
import { useCanvasStore } from "../store/canvas-store";
import {
  readCanvasObjectsFromClipboardData,
  readCanvasObjectsFromHtml,
  readCanvasObjectsFromText,
} from "../../../lib/canvas-clipboard";
import {
  TABLE_MAX_COLUMNS,
  TABLE_MAX_ROWS,
  parseMarkdownTable,
  type CanvasObject,
} from "@refinement-canvas/domain";
import { isLikelyPasteUrl } from "../../../lib/paste-url";
import { htmlFragmentToLinkText } from "../../../lib/paste-links";
import {
  htmlTableToCells,
  tsvToCells,
  type ParsedClipboardTable,
} from "../../../lib/rich-table-clipboard";
import { isEditingEventContext } from "./editing-target";

interface Handlers {
  readonly onPasteImage: (file: File, position: { x: number; y: number }) => void;
  readonly onPasteUrl: (url: string, position: { x: number; y: number }) => void;
  readonly onPasteCanvasObjects: (
    objects: readonly CanvasObject[],
    position: { x: number; y: number }
  ) => void;
  /** GFM 表を、行列上限で解析済みの表図形として配置する。 */
  readonly onPasteTable: (
    cells: readonly (readonly string[])[],
    truncated: boolean,
    position: { x: number; y: number }
  ) => void;
  /** キャンバス固有形式・画像・URL のいずれでもない文字列をテキスト図形として配置する。 */
  readonly onPasteText: (text: string, position: { x: number; y: number }) => void;
  /**
   * 選択中の付箋・テキスト図形の本文を URL のリンクへ変換する。
   * 変換または利用者への通知を行った場合は true を返し、呼び出し側は
   * OGP リンクカードの作成へ進まない。対象が無ければ false を返す。
   */
  readonly onPasteUrlIntoSelectedTexts?: ((url: string) => boolean) | undefined;
  /** 参照専用（埋め込み）。true で貼り付け配置を無効化する。 */
  readonly readOnly?: boolean | undefined;
}

/** コンテキストメニュー等、プログラムから貼り付けを起こす際に渡す配置ハンドラ。 */
export type ProgrammaticPasteHandlers = Pick<
  Handlers,
  "onPasteImage" | "onPasteUrl" | "onPasteCanvasObjects" | "onPasteTable" | "onPasteText"
>;

const MARKDOWN_TABLE_LIMITS = { maxColumns: TABLE_MAX_COLUMNS, maxRows: TABLE_MAX_ROWS } as const;

/** text/html の取得・解析失敗を後続形式へ安全にフォールバックさせる。 */
function readHtmlTable(
  readHtml: () => string | undefined,
  limits: typeof MARKDOWN_TABLE_LIMITS
): ParsedClipboardTable | null {
  try {
    const html = readHtml();
    return html === undefined || html.length === 0 ? null : htmlTableToCells(html, limits);
  } catch {
    return null;
  }
}

/**
 * クリップボードの text/html を、リンクを保ったテキスト図形の本文へ変換する。
 * 変換できない場合（HTML が無い / http(s) のアンカーが 1 つも無い / 結果が空白だけ）は
 * undefined を返し、呼び出し側は従来どおり text/plain の経路へ進む。
 *
 * Teams のように text/plain へ表示文字だけを書き、URL を text/html のアンカーにしか
 * 持たせないアプリからのコピーで、URL が落ちるのを防ぐのが目的。変換規則そのものは
 * 本文編集中の貼り付け（use-object-text-editor）と同じ htmlFragmentToLinkText を共有する。
 *
 * HTML は遅延取得（readHtml）で受け取り、取得の失敗と解析の失敗を同じ try で吸収する。
 */
function htmlAnchorsToPastedText(readHtml: () => string | undefined): string | undefined {
  let converted: string | undefined;
  try {
    const html = readHtml();
    if (html === undefined || html.length === 0) return undefined;
    converted = htmlFragmentToLinkText(html);
  } catch {
    // text/html を取得できない、または DOMParser が例外を投げる環境でも、
    // 従来の text/plain 経路で貼り付けを成立させる。
    return undefined;
  }
  if (converted === undefined) return undefined;
  // 既存の通常テキスト分岐と同じく前後の空白を落とし、空になれば変換しなかった扱いにする。
  const trimmed = converted.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function usePasteHandler(handlers: Handlers): void {
  const readOnly = handlers.readOnly ?? false;
  useEffect(() => {
    if (readOnly) return undefined;
    const onPaste = (event: ClipboardEvent): void => {
      // 入力要素にフォーカスがある場合は通常のペースト動作に任せる。
      // 直接の target だけでなく伝播経路と現在のフォーカスも見ることで、contentEditable の
      // 子要素から伝播した paste や retarget された paste でも、キャンバスへの配置
      // （オブジェクト複製 / 画像配置 / OGP リンクカード作成）を起こさない。
      // preventDefault もしないため、ローカル編集器の onPaste が本文へ貼り付けを完遂できる。
      if (isEditingEventContext(event)) return;

      const data = event.clipboardData;
      if (!data) return;

      const position = useCanvasStore.getState().lastPointerStagePosition ?? { x: 0, y: 0 };

      // 1) キャンバスオブジェクトがあれば、別キャンバスでも同じ内容を複製する。
      let canvasObjects: readonly CanvasObject[] = [];
      try {
        canvasObjects = readCanvasObjectsFromClipboardData(data);
      } catch {
        // この復元は text/html も読むため、その形式の取得だけが例外を投げる環境では、
        // 保護しないと以降の候補（画像・URL・表・リンク・通常テキスト）へ到達できなくなる。
        // 「その形式は無かった」として、初期値の空配列のまま後続の分岐へ進める。
      }
      if (canvasObjects.length > 0) {
        event.preventDefault();
        handlers.onPasteCanvasObjects(canvasObjects, position);
        return;
      }

      // 2) 画像があれば優先
      for (const item of Array.from(data.items)) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            event.preventDefault();
            handlers.onPasteImage(file, position);
            return;
          }
        }
      }

      // 3) テキストが URL なら、選択中の付箋・テキスト図形の本文リンク化を優先し、
      //    対象が無ければ OGP リンクカード化する。
      const text = data.getData("text/plain");
      if (text && isLikelyPasteUrl(text)) {
        // どちらの経路でもブラウザ既定の貼り付けは不要なので、分岐の前に抑止する。
        event.preventDefault();
        const url = text.trim();
        if (handlers.onPasteUrlIntoSelectedTexts?.(url) === true) return;
        handlers.onPasteUrl(url, position);
        return;
      }

      // 4) URL ではない GFM 表は、表図形として配置する。
      const table = parseMarkdownTable(text, MARKDOWN_TABLE_LIMITS);
      if (table !== null) {
        event.preventDefault();
        handlers.onPasteTable(table.cells, table.truncated, position);
        return;
      }

      // 5) 外部アプリ由来の HTML 表を表図形として配置する。
      const htmlTable = readHtmlTable(() => data.getData("text/html"), MARKDOWN_TABLE_LIMITS);
      if (htmlTable !== null) {
        event.preventDefault();
        handlers.onPasteTable(htmlTable.cells, htmlTable.truncated, position);
        return;
      }

      // 6) TSV は、GFM / HTML 表ではない場合に表図形として配置する。
      const tsvTable = tsvToCells(text, MARKDOWN_TABLE_LIMITS);
      if (tsvTable !== null) {
        event.preventDefault();
        handlers.onPasteTable(tsvTable.cells, tsvTable.truncated, position);
        return;
      }

      // 7) text/html にハイパーリンクがあれば、[表示文字](URL) 形式へ変換して配置する。
      //    1〜6 より後に置くのは、既存の貼り付け結果を 1 つも変えないため。とくに単一 URL の
      //    コピーでは多くのブラウザーが text/html にもアンカーを書き込むため、3 より前に置くと
      //    OGP リンクカードが作られなくなる。表を含む HTML でも 4 の表図形化を妨げない。
      //    8 より前に置くのは本件の目的そのもの（text/plain に無い URL を落とさない）。
      const linkedText = htmlAnchorsToPastedText(() => data.getData("text/html"));
      if (linkedText !== undefined) {
        event.preventDefault();
        handlers.onPasteText(linkedText, position);
        return;
      }

      // 8) 既存形式のどれにも当たらない文字列は、前後の空白を除いてテキスト図形にする。
      const plainText = text.trim();
      if (plainText.length > 0) {
        event.preventDefault();
        handlers.onPasteText(plainText, position);
      }
    };

    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [handlers, readOnly]);
}

/**
 * コンテキストメニューの「貼り付け」から、非同期 Clipboard API（navigator.clipboard）経由で
 * 貼り付けを実行する。Ctrl+V（paste イベント）と同じ優先順位で、
 * (1) HTML 埋め込み / text/plain のキャンバスオブジェクト → (2) 画像 → (3) URL →
 * (4) GFM 表 → (5) HTML 表 → (6) TSV → (7) text/html のハイパーリンク →
 * (8) 通常テキストの順に振り分ける。
 * 戻り値: "pasted"=貼り付けた / "empty"=対象なし / "unavailable"=API 非対応・権限拒否。
 */
export async function performClipboardPaste(
  handlers: ProgrammaticPasteHandlers,
  position: { x: number; y: number }
): Promise<"pasted" | "empty" | "unavailable"> {
  if (typeof navigator === "undefined" || !navigator.clipboard) return "unavailable";

  // リッチな read()（画像 Blob / テキストの両取得）が使える環境を優先する。
  const read = navigator.clipboard.read?.bind(navigator.clipboard);
  if (read) {
    try {
      const items = await read();
      // 1a) text/html の埋め込みオブジェクトを最優先で復元する。
      // 後段（5, 7）の表・ハイパーリンク変換でも同じ HTML を使うため、ここで読めたものを控えて二重取得を避ける。
      // read() は複数の ClipboardItem を返し得るので、活性判定（probeClipboardPasteable）が
      // 「どれかに text/html がある」で活性化するのと対になるよう、読めた断片をすべて控える。
      const htmlFragments: string[] = [];
      for (const item of items) {
        if (!item.types.includes("text/html")) continue;
        try {
          const html = await (await item.getType("text/html")).text();
          if (html.length > 0) htmlFragments.push(html);
          const objects = readCanvasObjectsFromHtml(html);
          if (objects.length > 0) {
            handlers.onPasteCanvasObjects(objects, position);
            return "pasted";
          }
        } catch {
          // HTML 形式だけを取得できない環境でも、画像などの後続候補は貼り付けられるようにする。
        }
      }
      // 1b) text/plain のフォールバック接頭辞からオブジェクトを復元する。
      for (const item of items) {
        if (!item.types.includes("text/plain")) continue;
        const text = await (await item.getType("text/plain")).text();
        const objects = readCanvasObjectsFromText(text);
        if (objects.length > 0) {
          handlers.onPasteCanvasObjects(objects, position);
          return "pasted";
        }
      }
      // 2) 画像。
      for (const item of items) {
        const imageType = item.types.find((t) => t.startsWith("image/"));
        if (!imageType) continue;
        const blob = await item.getType(imageType);
        const file = new File([blob], `pasted-image.${mimeToExtension(imageType)}`, {
          type: imageType,
        });
        handlers.onPasteImage(file, position);
        return "pasted";
      }
      // 3) URL テキスト。
      for (const item of items) {
        if (!item.types.includes("text/plain")) continue;
        const text = (await (await item.getType("text/plain")).text()).trim();
        if (isLikelyPasteUrl(text)) {
          handlers.onPasteUrl(text, position);
          return "pasted";
        }
      }
      // 4) GFM 表。
      for (const item of items) {
        if (!item.types.includes("text/plain")) continue;
        const text = await (await item.getType("text/plain")).text();
        const table = parseMarkdownTable(text, MARKDOWN_TABLE_LIMITS);
        if (table !== null) {
          handlers.onPasteTable(table.cells, table.truncated, position);
          return "pasted";
        }
      }
      // 5) 外部アプリ由来の HTML 表。
      for (const html of htmlFragments) {
        const htmlTable = readHtmlTable(() => html, MARKDOWN_TABLE_LIMITS);
        if (htmlTable !== null) {
          handlers.onPasteTable(htmlTable.cells, htmlTable.truncated, position);
          return "pasted";
        }
      }
      // 6) TSV。
      for (const item of items) {
        if (!item.types.includes("text/plain")) continue;
        const text = await (await item.getType("text/plain")).text();
        const table = tsvToCells(text, MARKDOWN_TABLE_LIMITS);
        if (table !== null) {
          handlers.onPasteTable(table.cells, table.truncated, position);
          return "pasted";
        }
      }
      // 7) text/html のハイパーリンク（Ctrl+V と同じく、1〜6 のどれにも当たらない場合だけ）。
      //    先頭の断片が書式だけで後続の断片にアンカーがある場合もあるため、変換できる最初の
      //    断片を採用する。1 つ目で解析が失敗しても、後続の断片で貼り付けを成立させる。
      let linkedText: string | undefined;
      for (const html of htmlFragments) {
        linkedText = htmlAnchorsToPastedText(() => html);
        if (linkedText !== undefined) break;
      }
      if (linkedText !== undefined) {
        handlers.onPasteText(linkedText, position);
        return "pasted";
      }
      // 8) 通常テキスト。
      for (const item of items) {
        if (!item.types.includes("text/plain")) continue;
        const text = (await (await item.getType("text/plain")).text()).trim();
        if (text.length > 0) {
          handlers.onPasteText(text, position);
          return "pasted";
        }
      }
      return "empty";
    } catch {
      // read() が拒否 / 失敗した場合は readText() のテキスト経路へフォールバックする。
    }
  }

  // readText() フォールバック（テキスト系のみ。画像は取得できない）。
  try {
    const text = (await navigator.clipboard.readText()).trim();
    if (text.length === 0) return "empty";
    const objects = readCanvasObjectsFromText(text);
    if (objects.length > 0) {
      handlers.onPasteCanvasObjects(objects, position);
      return "pasted";
    }
    if (isLikelyPasteUrl(text)) {
      handlers.onPasteUrl(text, position);
      return "pasted";
    }
    const table = parseMarkdownTable(text, MARKDOWN_TABLE_LIMITS);
    if (table !== null) {
      handlers.onPasteTable(table.cells, table.truncated, position);
      return "pasted";
    }
    const tsvTable = tsvToCells(text, MARKDOWN_TABLE_LIMITS);
    if (tsvTable !== null) {
      handlers.onPasteTable(tsvTable.cells, tsvTable.truncated, position);
      return "pasted";
    }
    handlers.onPasteText(text, position);
    return "pasted";
  } catch {
    return "unavailable";
  }
}

/**
 * クリップボードに貼り付け可能な内容（画像・テキスト）があるかを best-effort で判定する。
 * コンテキストメニューの「貼り付け」項目の活性/非活性の初期判定に使う。
 * - "has-content": 読み取れて内容がある → 活性
 * - "empty"      : 読み取れたが空       → 非活性（グレーアウト）
 * - "unknown"    : API 非対応・権限拒否等で判定不可 → 呼び出し側は楽観的に活性化し、
 *                  クリック時の performClipboardPaste の結果（empty ならトースト）に委ねる
 *
 * text/html は、アンカーの有無を解析せず「形式があれば貼り付けられるかもしれない」として
 * 楽観的に "has-content" にする。performClipboardPaste は text/plain を持たない
 * text/html のアンカーも貼り付けるため、ここで解析を省いて "empty" にすると、
 * Ctrl+V では貼り付けられる内容をコンテキストメニューからは実行できなくなる。
 * アンカーが無ければ貼り付け時に "empty" が返り、既存のトースト通知へ落ちる。
 */
export async function probeClipboardPasteable(): Promise<"has-content" | "empty" | "unknown"> {
  if (typeof navigator === "undefined" || !navigator.clipboard) return "unknown";
  const read = navigator.clipboard.read?.bind(navigator.clipboard);
  if (read) {
    try {
      const items = await read();
      for (const item of items) {
        if (item.types.some((t) => t.startsWith("image/"))) return "has-content";
        // アンカーの有無の解析は貼り付け時に行い、ここでは形式の存在だけで活性に保つ。
        if (item.types.includes("text/html")) return "has-content";
        if (item.types.includes("text/plain")) {
          const text = (await (await item.getType("text/plain")).text()).trim();
          // performClipboardPaste が受け付けるキャンバスオブジェクト・URL・通常テキストを
          // まとめて「貼付可」とする。呼び出し側で trim 済みなので空白だけなら空になる。
          if (isPasteableText(text)) return "has-content";
        }
      }
      return "empty";
    } catch {
      // fall through to readText()
    }
  }
  try {
    return isPasteableText((await navigator.clipboard.readText()).trim()) ? "has-content" : "empty";
  } catch {
    return "unknown";
  }
}

// 通常テキストもテキスト図形にできるため、trim 済みの文字列があれば貼り付け可能。
function isPasteableText(text: string): boolean {
  return text.length > 0;
}

function mimeToExtension(mimeType: string): string {
  const subtype = mimeType.split("/")[1] ?? "png";
  return subtype === "jpeg" ? "jpg" : subtype;
}
